import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MessageCircle, Lock, Unlock, ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Avatar, EmptyState, StatusBadge } from "@/components/common";
import { CreatorPicker } from "@/components/CreatorPicker";
import { ChatView } from "@/components/ChatView";
import { api, errMsg } from "@/lib/api";
import { ago } from "@/lib/format";
import { cn } from "@/lib/utils";

function Thread({ uid, onChanged, onBack }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get(`/admin/threads/${uid}`)).data); } catch (e) { toast.error(errMsg(e)); }
  }, [uid]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  if (!data) return <div className="h-full grid place-items-center text-sm text-muted-foreground">Loading…</div>;
  const send = async (p) => { await api.post(`/admin/threads/${uid}/messages`, p); await load(); onChanged(); };
  const toggle = async () => {
    await api.post(`/admin/threads/${uid}/status`, { status: data.thread.status === "open" ? "closed" : "open" });
    load(); onChanged();
  };
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <button onClick={onBack} className="md:hidden"><ArrowLeft className="h-4 w-4" /></button>
        <Avatar src={data.creator?.profile_picture} name={data.creator?.full_name} className="h-9 w-9" />
        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{data.creator?.full_name}</p><p className="text-[11px] text-muted-foreground">@{data.creator?.instagram_username}</p></div>
        <StatusBadge status={data.thread.status === "open" ? "APPROVED" : "CANCELLED"} />
        <Button size="sm" variant="outline" className="rounded-full" onClick={toggle} data-testid="thread-toggle-status">
          {data.thread.status === "open" ? <><Lock className="h-3.5 w-3.5 mr-1.5" />Close</> : <><Unlock className="h-3.5 w-3.5 mr-1.5" />Reopen</>}
        </Button>
      </div>
      <div className="flex-1 min-h-0"><ChatView messages={data.messages} mineRole="admin" onSend={send} theirName={data.creator?.full_name} closed={data.thread.status === "closed"} /></div>
    </div>
  );
}

export default function AdminMessages() {
  const [params, setParams] = useSearchParams();
  const active = params.get("c");
  const [q, setQ] = useState("");
  const [threads, setThreads] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const load = useCallback(async () => {
    try { setThreads((await api.get("/admin/threads", { params: { q } })).data.items); } catch (e) { toast.error(errMsg(e)); }
  }, [q]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const open = (uid) => setParams({ c: uid });
  return (
    <div>
      <PageHeader eyebrow="Support" title="Messages" desc="Private 1:1 conversations with creators.">
        <Button className="rounded-full" onClick={() => setStartOpen(true)} data-testid="start-conversation-button"><Plus className="h-4 w-4 mr-1.5" />New conversation</Button>
      </PageHeader>
      <div className="uc-card h-[70vh] grid md:grid-cols-[320px_1fr] overflow-hidden" data-testid="admin-messages">
        <div className={cn("border-r border-border flex flex-col min-h-0", active && "hidden md:flex")}>
          <div className="p-3 border-b border-border relative">
            <Search className="h-4 w-4 absolute left-6 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators" className="pl-9 rounded-xl bg-background" data-testid="threads-search-input" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {threads === null ? <p className="p-4 text-sm text-muted-foreground">Loading…</p> : threads.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center" data-testid="threads-empty">No messages yet.</p> :
              threads.map((t) => (
                <button key={t.id} onClick={() => open(t.creator_id)} data-testid={`thread-${t.creator_id}`}
                  className={cn("w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/40 flex gap-3", active === t.creator_id && "bg-primary/5")}>
                  <Avatar name={t.creator_name} className="h-9 w-9" />
                  <span className="flex-1 min-w-0">
                    <span className="flex justify-between gap-2"><span className="text-sm font-medium truncate">{t.creator_name}</span><span className="text-[10px] text-muted-foreground shrink-0">{ago(t.last_message_at)}</span></span>
                    <span className="flex justify-between gap-2"><span className="text-xs text-muted-foreground truncate">{t.status === "closed" ? "Closed · " : ""}{t.last_message || "No messages"}</span>
                      {t.unread_admin > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full gold-bg text-black text-[10px] font-bold grid place-items-center">{t.unread_admin}</span>}</span>
                  </span>
                </button>
              ))}
          </div>
        </div>
        <div className={cn("min-h-0", !active && "hidden md:block")}>
          {active ? <Thread key={active} uid={active} onChanged={load} onBack={() => setParams({})} /> :
            <div className="h-full grid place-items-center p-6"><EmptyState icon={MessageCircle} title="Select a conversation" desc="Or start a new one with any creator." /></div>}
        </div>
      </div>
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="bg-card border-border rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Start conversation</DialogTitle></DialogHeader>
          <CreatorPicker selected={picked} onChange={(s) => { setPicked([]); setStartOpen(false); if (s[0]) open(s[0].user_id); }} testid="start-thread-picker" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
